import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { UserProfileService, UserProfileStatsData } from '../services/userProfileService';
import { getImageUrl } from '../services/api';

export default function UserProfileTab() {
  const { user, refreshUser } = useAuth();

  // --- Name fields ---
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // --- Password fields ---
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // --- Avatar ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // --- Stats ---
  const [stats, setStats] = useState<UserProfileStatsData | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Sync name fields when user refreshes
  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
  }, [user?.firstName, user?.lastName]);

  // Load stats on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await UserProfileService.getStats();
        if (!cancelled) setStats(data);
      } catch {
        // silent
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  // --- Handlers ---

  const handleSaveName = useCallback(async () => {
    try {
      setNameSaving(true);
      setNameMsg(null);
      await UserProfileService.updateProfile({ firstName, lastName });
      await refreshUser();
      setNameMsg({ type: 'success', text: 'Данните са запазени.' });
    } catch {
      setNameMsg({ type: 'error', text: 'Неуспешно запазване.' });
    } finally {
      setNameSaving(false);
    }
  }, [firstName, lastName, refreshUser]);

  const handleChangePassword = useCallback(async () => {
    setPwMsg(null);
    if (newPassword !== confirmPassword) {
      setPwMsg({ type: 'error', text: 'Паролите не съвпадат.' });
      return;
    }
    if (newPassword.length < 6) {
      setPwMsg({ type: 'error', text: 'Паролата трябва да е поне 6 символа.' });
      return;
    }
    try {
      setPwSaving(true);
      const res = await UserProfileService.changePassword({ currentPassword, newPassword, confirmPassword });
      setPwMsg({ type: 'success', text: res.message });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Грешка при смяна на паролата.';
      setPwMsg({ type: 'error', text: msg });
    } finally {
      setPwSaving(false);
    }
  }, [currentPassword, newPassword, confirmPassword]);

  const handleAvatarChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setAvatarUploading(true);
      setAvatarMsg(null);
      await UserProfileService.uploadAvatar(file);
      await refreshUser();
      setAvatarMsg({ type: 'success', text: 'Аватарът е обновен.' });
    } catch {
      setAvatarMsg({ type: 'error', text: 'Неуспешно качване.' });
    } finally {
      setAvatarUploading(false);
      // Reset input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [refreshUser]);

  // --- Initials fallback ---
  const initials = [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join('').toUpperCase() || '?';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* ---- Avatar ---- */}
      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={user?.avatar ? getImageUrl(user.avatar) : undefined}
            sx={{ width: 80, height: 80, fontSize: 32 }}
          >
            {initials}
          </Avatar>
          <Box>
            <Button
              variant="outlined"
              size="small"
              disabled={avatarUploading}
              onClick={() => fileInputRef.current?.click()}
              startIcon={avatarUploading ? <CircularProgress size={16} /> : undefined}
            >
              Промени
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleAvatarChange}
            />
          </Box>
        </CardContent>
        {avatarMsg && (
          <Alert severity={avatarMsg.type} sx={{ mx: 2, mb: 2 }}>{avatarMsg.text}</Alert>
        )}
      </Card>

      {/* ---- Personal info ---- */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Лични данни
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Име"
                fullWidth
                size="small"
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); setNameMsg(null); }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Фамилия"
                fullWidth
                size="small"
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); setNameMsg(null); }}
              />
            </Grid>
          </Grid>
          {nameMsg && (
            <Alert severity={nameMsg.type} sx={{ mt: 2 }}>{nameMsg.text}</Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveName}
              disabled={nameSaving}
              startIcon={nameSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Запази
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ---- Password change ---- */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Смяна на парола
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Текуща парола"
                type="password"
                fullWidth
                size="small"
                value={currentPassword}
                onChange={(e) => { setCurrentPassword(e.target.value); setPwMsg(null); }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Нова парола"
                type="password"
                fullWidth
                size="small"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPwMsg(null); }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Потвърди парола"
                type="password"
                fullWidth
                size="small"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPwMsg(null); }}
              />
            </Grid>
          </Grid>
          {pwMsg && (
            <Alert severity={pwMsg.type} sx={{ mt: 2 }}>{pwMsg.text}</Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleChangePassword}
              disabled={pwSaving || !currentPassword || !newPassword || !confirmPassword}
              startIcon={pwSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Смени паролата
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ---- Statistics ---- */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Статистика
          </Typography>

          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : stats ? (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="h5" fontWeight={700}>{stats.totalSessions}</Typography>
                    <Typography variant="caption" color="text.secondary">Тренировки</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="h5" fontWeight={700}>
                      {stats.totalWeightMoved.toLocaleString()} кг
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Общо тежест</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="h5" fontWeight={700}>
                      {stats.sessionsPerWeek.length > 0
                        ? (stats.sessionsPerWeek.reduce((s, w) => s + w.count, 0) / stats.sessionsPerWeek.length).toFixed(1)
                        : '0'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Средно/седмица</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="h5" fontWeight={700}>
                      {stats.memberSince ? new Date(stats.memberSince).toLocaleDateString('bg-BG') : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Член от</Typography>
                  </Box>
                </Grid>
              </Grid>

              {stats.sessionsPerWeek.length > 0 && (
                <>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                    Тренировки на седмица (последни 12 седмици)
                  </Typography>
                  <Box sx={{ width: '100%', height: 250 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.sessionsPerWeek} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="weekLabel" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8 }}
                          labelStyle={{ color: '#e2e8f0' }}
                          formatter={(value) => [`${value}`, 'Тренировки']}
                        />
                        <Bar dataKey="count" fill="#38bdf8" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              )}
            </>
          ) : (
            <Typography color="text.secondary">Няма налична статистика.</Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
